import { API } from '../../api';
import {
  Button,
  Card,
  Col,
  Input,
  PageHeader,
  Row,
  Space,
  Switch,
  Table,
  Typography,
  message,
} from 'antd';
import ContractContentsModal from './contractContentsModal';
import React, { useEffect, useState } from 'react';

const { Text } = Typography;

message.config({
  duration: 3,
  maxCount: 3,
  rtl: false,
});

const ClientDataContracts = ({ client }) => {
  const [dataContract, setDataContract] = useState('{}');
  const [showContractDetailsModal, setShowContractDetailsModal] = useState(false);
  const [allDataContracts, setAllDataContracts] = useState(null);
  const [linkedContracts, setLinkedContracts] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filterLinkedContracts, setFilterLinkedContracts] = useState(true);

  const fetchDataContracts = async () => {
    try {
      const { data: dataContracts } = await API.get('/dataContract');
      const { data: clientInfo } = await API.get(`/client/${client.id}`);
      setLinkedContracts(clientInfo.data_contracts);
      setAllDataContracts(dataContracts);
    } catch (error) {
      message.error('There was a problem fetching the data contracts');
      setLinkedContracts([]);
      setAllDataContracts([]);
    }
  };

  const handleViewContractClick = contractId => {
    const contractToDisplay = allDataContracts.find(({ id }) => id === contractId);
    setDataContract(contractToDisplay);
    setShowContractDetailsModal(true);
  };

  const handleLinkContractClick = async (contractId, isLinkedToClient, baseSchema) => {
    try {
      let newDataContracts = linkedContracts.map(({ id }) => id);

      if (isLinkedToClient) {
        newDataContracts = newDataContracts.filter(id => id !== contractId);
      } else {
        const duplicateSchemaLinking = linkedContracts.find(
          lc => lc.data_schema.baseSchema === baseSchema,
        );

        if (duplicateSchemaLinking) {
          return message.error(`Cannot link multiple contracts with baseSchema ${baseSchema}`);
        }

        newDataContracts.push(contractId);
      }

      await API.patch(`/client/${client.id}`, {
        data_contracts: newDataContracts,
      });

      const messageText = isLinkedToClient ? 'Unlinked' : 'Linked';

      message.success(`Contract ${messageText}!`);
      return fetchDataContracts();
    } catch (err) {
      const errMessageText = isLinkedToClient ? 'unlinking' : 'linking';
      return message.error(`There was a problem ${errMessageText} the data contract`);
    }
  };

  const columnSort = (a, b) => {
    if (!!a.name && !!b.name) {
      return a.name.localeCompare(b.name);
    }

    return null;
  };

  useEffect(() => {
    fetchDataContracts();
  }, []);

  const dataContractsDisplayed = () => {
    let contractsToDisplay = [];

    if (allDataContracts !== null) {
      contractsToDisplay = allDataContracts.filter(({ name }) => {
        return name.toLowerCase().includes(searchText.toLowerCase());
      });

      if (filterLinkedContracts) {
        contractsToDisplay = contractsToDisplay.filter(({ id }) => {
          return linkedContracts.find(clientContract => {
            return clientContract.id === id;
          });
        });
      }
    }

    return contractsToDisplay;
  };

  const tableName = `${filterLinkedContracts ? 'Linked' : 'All'} Contracts`;
  const linkedContractIds = linkedContracts ? linkedContracts.map(({ id }) => id) : [];
  return (
    <>
      <PageHeader
        className="site-page-header"
        onBack={() => window.history.back()}
        title="Link Data Contracts"
      />
      <Row>
        <Col span={10}>
          <p>View and link data contracts</p>
        </Col>
      </Row>
      <Row gutter={[8, 8]}>
        <Col style={{ marginBottom: 5 }}>
          <Input
            data-cy="search-clients-input"
            style={{ width: '350px' }}
            addonBefore="Search:"
            onChange={e => setSearchText(e.target.value)}
          />
          <Text style={{ paddingLeft: '20px' }}>Show All Contracts:</Text>
          <Switch
            size="small"
            style={{ marginLeft: '8px' }}
            onChange={() => {
              setFilterLinkedContracts(!filterLinkedContracts);
            }}
          />
          ;
        </Col>
      </Row>
      <Row justify="start" align="top" gutter={[20, 16]}>
        <Col span={24}>
          <Card title={tableName}>
            <Table
              columns={[
                {
                  title: 'Name',
                  dataIndex: 'name',
                  defaultSortOrder: 'ascend',
                  sorter: columnSort,
                },
                {
                  title: 'Version',
                  dataIndex: 'version',
                  sorter: columnSort,
                },
                {
                  title: 'Description',
                  dataIndex: 'description',
                  sorter: columnSort,
                },
                {
                  title: '',
                  width: 100,
                  render: (_value, { id, name, data_schema: dataSchema }) => {
                    const isLinkedToClient = linkedContractIds.includes(id);
                    const linkBtnText = isLinkedToClient ? 'Unlink' : 'Link';
                    const linkBtnType = isLinkedToClient ? 'danger' : 'primary';
                    return (
                      <Space size="middle">
                        <Button
                          data-cy={name}
                          style={{ marginRight: '2px' }}
                          onClick={() => handleViewContractClick(id)}
                          size="small"
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          style={{ marginRight: '2px' }}
                          type={linkBtnType}
                          onClick={() =>
                            handleLinkContractClick(id, isLinkedToClient, dataSchema.baseSchema)
                          }
                        >
                          {linkBtnText} Contract
                        </Button>
                      </Space>
                    );
                  },
                },
              ]}
              rowKey="id"
              className="dataContract-table"
              pagination={{ pageSize: 8, size: 'small' }}
              size="medium"
              loading={false}
              dataSource={dataContractsDisplayed()}
            />
          </Card>
        </Col>
      </Row>
      <ContractContentsModal
        isOpen={showContractDetailsModal}
        closeModal={() => {
          setDataContract('{}');
          setShowContractDetailsModal(false);
        }}
        contractContents={dataContract}
      />
    </>
  );
};

export default ClientDataContracts;
